/* eslint-disable react/require-default-props */
import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { RaisedButton, Dialog, MenuItem, IconButton, DatePicker } from 'material-ui'; 
import TextField from 'material-ui/TextField';
import SelectField from 'material-ui/SelectField';
import ControlPointIcon from 'material-ui/svg-icons/image/control-point';
import ContentRemoveIcon from 'material-ui/svg-icons/content/remove-circle-outline';

import portActivities from 'data/portActivities';
import Emails from './Emails';
import Phones from './Phones';
import { ContextCountries } from '../../../PortManagement';
import CompaniesAutoComplete from '../../../../../common/CompaniesAutoComplete';
import UserAutoComplete from '../../../../../common/UserAutoComplete';

const styles = {
    dialog: {
        contentStyle: { width: '843px', padding: '20px' },
    },
    textField: {
        floatingLabelStyle: { fontSize: '12px' },
    },
    btn: {
        labelStyle: { color: '#fff' },
    },
};

const emptyLimitation = {
    maxDraught: undefined,
    maxBeam: undefined,
    maxLOA: undefined,
    maxDWT: undefined,
    channelDepth: undefined,
    anchorageDepth: undefined,
    berth: undefined,
};

const LimitationField = ({
    control,
    name,
    label,
    width = '14%',
    marginLeft = '0px',
    maxValue = 100,
}) => (
    <Controller
        name={name}
        control={control}
        defaultValue=""
        rules={{
            validate: value => !value || (Number(value) >= 0 && Number(value) <= maxValue) || `Must be between 0 and ${maxValue}`,
        }}
        render={({ onChange, value, error }) => (
            <TextField
                floatingLabelText={label}
                floatingLabelStyle={styles.textField.floatingLabelStyle}
                style={{ width, marginLeft }}
                onChange={e => onChange(e.target.value)}
                value={value}
                errorText={error && error.message}
            />
        )}
    />
);

const PortLimitations = ({
    control, fields, append, remove, 
}) => {
    const limitationFields = [
        { name: 'berth', label: 'Berth', noValidation: true },
        { name: 'maxDraught', label: 'Max draft, m:' },
        { name: 'maxBeam', label: 'Max beam, m:' },
        { name: 'maxLOA', label: 'Max LOA, m:' },
        { name: 'maxDWT', label: 'Max DWT, m:', maxValue: 1000000 },
        { name: 'channelDepth', label: 'Channel Depth, m:' },
    ];

    return (
        <div>
            {fields.map((field, index) => (
                <div className='row' key={field.id}>
                    <div className='col-xs-12'>
                        {limitationFields.map((fieldConfig, fieldIndex) => (
                            <LimitationField
                                key={fieldConfig.name}
                                control={control}
                                name={`limitations.${index}.${fieldConfig.name}`}
                                label={fieldConfig.label}
                                marginLeft={fieldIndex > 0 ? '10px' : '0px'}
                                maxValue={fieldConfig.maxValue}
                            />
                        ))}
                        <div className='center-xs' style={{ display: 'inline-flex', width: '8%' }}>
                            {index === 0 ? (
                                <IconButton onClick={() => append(emptyLimitation)}>
                                    <ControlPointIcon />
                                </IconButton>
                            ) : (
                                <IconButton onClick={() => remove(index)}>
                                    <ContentRemoveIcon />
                                </IconButton>
                            )}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

const EditServiceProvider = (props) => {
    const {
        open,
        onClose,
        onSubmit,
        serviceProvider,
        hasRecommended,
    } = props;

    const countries = useContext(ContextCountries);

    const canBeRecommended = React.useCallback(() => !hasRecommended, [hasRecommended]);

    const {
        control, handleSubmit, watch, errors, reset, setValue, register,
    } = useForm({
        defaultValues: serviceProvider || {
            activities: [],
            name: '',
            address: '',
            PIC: '',
            emails: [''],
            phones: [{ code: '', phone: '' }],
            limitations: [emptyLimitation],
            companyId: undefined,
            contactPersonId: undefined,
            recommended: undefined,
        },
        mode: 'onChange',
        shouldUnregister: false,
        resolver: (values) => {
            const _errors = {};
            
            if (values.emails) {
                const emailErrors = values.emails.map((email) => {
                    if (!email) return 'Email is required';
                    const re = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
                    return re.test(email) ? undefined : 'Invalid email address';
                });
                
                if (emailErrors.some(error => error)) {
                    _errors.emails = emailErrors;
                }
            }

            if (values.phones) {
                const phoneErrors = values.phones.map((phone) => {
                    const _err = {};
                    if (!phone.code) _err.code = 'Required';
                    if (!phone.phone) _err.phone = 'Required';
                    else if (!/^\d+$/.test(phone.phone)) _err.phone = 'Must be a number';
                    else if (phone.phone.length < 6 || phone.phone.length > 15) {
                        _err.phone = 'Must be between 7 and 15 digits';
                    }
                    return Object.keys(_err).length ? _err : undefined;
                });
                
                if (phoneErrors.some(error => error)) {
                    _errors.phones = phoneErrors;
                }
            }

            return {
                values,
                errors: _errors,
            };
        },
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'limitations',
    });

    const activities = watch('activities', []);

    useEffect(() => {
        register('recommended');
    }, [register]);

    useEffect(() => {
        if (!Array.isArray(activities)) return;
        if (!activities.includes('Port Agency')) {
            setValue('recommended', undefined);
        }
    }, [activities, setValue]);

    const onSubmitForm = (data) => {
        onSubmit({
            ...serviceProvider,
            ...data,
        });
        onClose();
    };

    useEffect(() => {
        if (serviceProvider) {
            reset(serviceProvider);
        }
    }, [serviceProvider, reset]);

    console.log(Array.isArray(activities) && 
    activities.includes('Port Agency') &&
    canBeRecommended());

    return (
        <React.Fragment>
            <Dialog
                modal={false}
                open={open}
                contentStyle={styles.dialog.contentStyle}
                onRequestClose={onClose}
                paperClassName="dialog-service-provider"
                bodyStyle={{ overflowY: 'auto' }}
            >
                <form onSubmit={handleSubmit(onSubmitForm)}>
                    <div className="title">
                        Edit Service Provider: <br />
                        Marine equipment & spare parts
                    </div>

                    <div className="row">
                        <div className="col-xs-6">
                            <Controller
                                name="activities"
                                control={control}
                                rules={{ required: 'Activities are required' }}
                                render={({ onChange, value }) => (
                                    <SelectField
                                        floatingLabelText='Activities'
                                        floatingLabelStyle={styles.textField.floatingLabelStyle}
                                        multiple
                                        value={value}
                                        onChange={(_e, _index, next) => {
                                            onChange(next);
                                            const arr = Array.isArray(next) ? next : [];
                                            if (!arr.includes('Port Agency')) {
                                                setValue('recommended', undefined);
                                            }
                                        }}
                                        fullWidth
                                        autoWidth
                                        errorText={errors.activities && errors.activities.message}
                                    >
                                        {portActivities.map((activity, index) => (
                                            <MenuItem
                                                key={index}
                                                value={activity}
                                                primaryText={activity}
                                            />
                                        ))}
                                    </SelectField>
                                )}
                            />
                        </div>
                    </div>
                    <div className='row'>
                        <div className='col-xs-6'>
                            <Controller
                                name="contactPersonId"
                                control={control}
                                rules={{
                                    validate: (val) => {
                                        if (!val) return true;
                                        if (!val._id && val.email) return 'Select user from list';
                                        if (val.email && val.email.length < 3) return 'User email must be at least 3 characters long';
                                        return true;
                                    },
                                }}
                                render={({ value }) => (
                                    <UserAutoComplete
                                        customValue={
                                            value && typeof value === 'object'
                                                ? (value.email || '')
                                                : (value || '')
                                        }
                                        onUserSelected={(val) => {
                                            const item = val && val.item ? val.item : null;
                                            if (!item) return;
                                            setValue('contactPersonId', { _id: item._id, email: item.email });
                                            if (item.company) {
                                                setValue('companyId', { _id: item.company._id, name: item.company.name });
                                                setValue('name', item.company.name);
                                            }
                                        }}
                                        onUserChanging={(text) => {
                                            setValue('contactPersonId', { _id: '', email: text });
                                        }}
                                        textFieldStyle={{ height: '72px' }}
                                        inputStyle={{ marginTop: '12px' }}
                                        hintText='Contact person'
                                        floatingLabelText='Contact person'
                                        withExtendedData
                                    />
                                )}
                            />
                        </div>
                        <div className='col-xs-6'>
                            <Controller
                                name="name"
                                control={control}
                                rules={{
                                    required: 'Company name is required',
                                    minLength: { value: 3, message: 'Minimum length is 3' },
                                    maxLength: { value: 266, message: 'Maximum length is 266' },
                                }}
                                render={({ value }) => (
                                    <CompaniesAutoComplete
                                        customValue={value || ''}
                                        onTextChange={(val) => {
                                            setValue('name', val);
                                            setValue('companyId', undefined);
                                            setValue('contactPersonId', { _id: '', email: '' });
                                        }}
                                        onCompanySelected={(val) => {
                                            const name = val && val.name ? val.name : '';
                                            const item = val && val.item ? val.item : null;
                                            const _id = item && item._id ? item._id : undefined;
                                            setValue('name', name);
                                            setValue('companyId', _id ? { _id, name } : null);
                                        }}
                                        floatingLabelText='Company name'
                                        floatingLabelStyle={styles.textField.floatingLabelStyle}
                                        errorText={errors.name && errors.name.message}
                                    />
                                )}
                            />
                        </div>
                    </div>

                    {activities.includes('Stevedoring Company') && (
                        <PortLimitations
                            control={control}
                            fields={fields}
                            append={append}
                            remove={remove}
                        />
                    )}

                    <div className="row">
                        <div className="col-xs-6">
                            <Controller
                                name="emails"
                                control={control}
                                defaultValue={['']}
                                rules={{
                                    validate: (emails) => {
                                        if (!emails || !emails.length) return 'At least one email is required';
                                        return emails.every((email) => {
                                            if (!email) return 'Email is required';
                                            const re = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
                                            return re.test(email) || 'Invalid email address';
                                        }) || 'Invalid email(s)';
                                    },
                                }}
                                render={({ onChange, value }) => (
                                    <Emails
                                        emails={value}
                                        onChange={onChange}
                                        error={errors.emails && errors.emails.message}
                                    />
                                )}
                            />
                        </div>
                        <div className="col-xs-6">
                            <Controller
                                control={control}
                                name="phones"
                                render={({ value, onChange }) => (
                                    <Phones
                                        phones={value}
                                        onChange={onChange}
                                        error={errors.phones && errors.phones.message}
                                        countries={countries}
                                    />
                                )}
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-xs-6">
                            <Controller
                                name="address"
                                control={control}
                                rules={{ required: 'Address is required' }}
                                render={({ onChange, value }) => (
                                    <TextField
                                        floatingLabelText='Address'
                                        floatingLabelStyle={styles.textField.floatingLabelStyle}
                                        fullWidth
                                        onChange={e => onChange(e.target.value)}
                                        value={value}
                                        errorText={errors.address && errors.address.message}
                                    />
                                )}
                            />
                        </div>
                        <div className="col-xs-6">
                            <Controller
                                name="PIC"
                                control={control}
                                rules={{ required: 'PIC is required' }}
                                render={({ onChange, value }) => (
                                    <TextField
                                        floatingLabelText='PIC'
                                        floatingLabelStyle={styles.textField.floatingLabelStyle}
                                        fullWidth
                                        onChange={e => onChange(e.target.value)}
                                        value={value}
                                        errorText={errors.PIC && errors.PIC.message}
                                    />
                                )}
                            />
                        </div>
                    </div>

                    {Array.isArray(activities) && 
                    activities.includes('Port Agency') &&
                    canBeRecommended() ? 
                        (
                            <div className='row'>
                                <div className='col-xs-5' />
                                <div className='col-xs-7'>
                                    <Controller
                                        name="recommended.endAt"
                                        control={control}
                                        rules={{
                                            validate: (val) => {
                                                if (!val) return true;
                                                const d = val instanceof Date ? val : new Date(val);
                                                const today = new Date();
                                                today.setHours(0, 0, 0, 0);
                                                return d.getTime() > today.getTime() || 'Select a future date';
                                            },
                                        }}
                                        render={({ onChange, value, error }) => {
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);

                                            const base = () => (value ? new Date(value) : new Date());
                                            const addMonths = (n) => {
                                                const d = base();
                                                d.setMonth(d.getMonth() + n);
                                                onChange(d);
                                            };
                                            const addYears = (n) => {
                                                const d = base();
                                                d.setFullYear(d.getFullYear() + n);
                                                onChange(d);
                                            };

                                            return (
                                                <div style={{
                                                    display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end', 
                                                }}
                                                >
                                                    <DatePicker
                                                        value={value ? new Date(value) : null}
                                                        onChange={(_e, date) => onChange(date)}
                                                        shouldDisableDate={dateSelected => dateSelected.getTime() <= today.getTime()}
                                                        errorText={error && error.message}
                                                        textFieldStyle={{ fontSize: '13px' }}
                                                        minDate={today}
                                                        floatingLabelText='Recommended until'
                                                        floatingLabelStyle={styles.textField.floatingLabelStyle}
                                                        fullWidth
                                                    />
                                                    <RaisedButton
                                                        label="+1 month"
                                                        type="button"
                                                        labelStyle={{ fontSize: '12px', padding: 0 }}
                                                        style={{ marginTop: '16px' }}
                                                        onClick={() => addMonths(1)}
                                                    />
                                                    <RaisedButton
                                                        label="+1 year"
                                                        type="button"
                                                        labelStyle={{ fontSize: '12px', padding: 0 }}
                                                        style={{ marginTop: '16px' }}
                                                        onClick={() => addYears(1)}
                                                    />
                                                    {value ? (
                                                        <IconButton
                                                            title="Remove recommended"
                                                            style={{
                                                                width: 32, height: 32, padding: 0, marginTop: '16px', 
                                                            }}
                                                            iconStyle={{ color: '#d32f2f' }}
                                                            onClick={() => onChange(null)}
                                                        >
                                                            <ContentRemoveIcon />
                                                        </IconButton>
                                                    ) : null}
                                                </div>
                                            );
                                        }}
                                    />
                                </div>
                            </div>
                        ) : null}

                    <div className='row'>
                        <div className='col-xs-12' style={{ display: 'flex', flexDirection: 'row-reverse', gap: '10px' }}>
                            <RaisedButton
                                label='Cancel'
                                primary
                                onClick={onClose}
                            />
                            <RaisedButton
                                label='Add'
                                backgroundColor='#7FC931'
                                labelStyle={styles.btn.labelStyle}
                                type="submit"
                            />
                        </div>
                    </div>
                </form>
            </Dialog>
        </React.Fragment>
    );
};

EditServiceProvider.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    serviceProvider: PropTypes.shape({
        activities: PropTypes.arrayOf(PropTypes.string),
        name: PropTypes.string,
        address: PropTypes.string,
        PIC: PropTypes.string,
        emails: PropTypes.arrayOf(PropTypes.string),
        phones: PropTypes.arrayOf(PropTypes.shape({
            code: PropTypes.string,
            phone: PropTypes.string,
        })),
        limitations: PropTypes.arrayOf(PropTypes.shape({
            berth: PropTypes.string,
            maxDraught: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            maxBeam: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            maxLOA: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            maxDWT: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            channelDepth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            anchorageDepth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        })),
    }),
};

export default EditServiceProvider;



// WEBPACK FOOTER //
// ./app/components/Regions/PortManagement/blocks/ServiceProviders/ServiceProvidersView/EditServiceProvider.jsx